<template>
  <div class="container-fluid px-4 mt-4">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h5 class="fw-bold mb-0">사용자 관리</h5>
      <span class="text-muted small">총 {{ pagination.totalCount }}명</span>
    </div>
    <div v-if="editSuccess" class="alert alert-success">{{ editSuccess }}</div>

    <div v-if="loadError" class="alert alert-danger">{{ loadError }}</div>

    <!-- 검색 입력 -->
    <div class="mb-3">
      <div class="input-group">
        <input 
          v-model="searchText" 
          type="text" 
          class="form-control" 
          placeholder="아이디, 이름, 차량번호로 검색..."
          @keyup.enter="search"
        />
        <button class="btn btn-outline-secondary" type="button" @click="search">
          검색
        </button>
        <button class="btn btn-outline-secondary" type="button" @click="resetSearch">
          초기화
        </button>
      </div>
      <small class="text-muted">검색어: {{ searchQuery || '전체' }}</small>
    </div>

    <div v-if="loading" class="text-center py-5 text-muted">불러오는 중...</div>

    <table v-else class="table table-hover align-middle">
      <thead class="table-dark">
        <tr>
          <th>#</th>
          <th>아이디</th>
          <th>이름</th>
          <th>차량번호</th>
          <th>전화번호</th>
          <th>구분</th>
          <th>신규/기존</th>
          <th>가입일</th>
          <th class="text-center">관리</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="users.length === 0">
          <td colspan="9" class="text-center text-muted py-4">등록된 사용자가 없습니다.</td>
        </tr>
        <tr v-for="u in users" :key="u.UserSeq">
          <td class="text-muted small">{{ u.UserSeq }}</td>
          <td>{{ u.UserID }}</td>
          <td>{{ u.Name }}</td>
          <td>{{ u.CarNumber || '-' }}</td>
          <td>{{ u.Tel || '-' }}</td>
          <td>
            <span :class="u.UserType === 'ADMIN' ? 'badge bg-danger' : 'badge bg-secondary'">
              {{ u.UserType }}
            </span>
          </td>
          <td>
            <span :class="u.IsExistingUser ? 'badge bg-success' : 'badge bg-secondary'">
              {{ u.IsExistingUser ? '기존' : '신규' }}
            </span>
          </td>
          <td class="small text-muted">{{ formatDate(u.RegDate) }}</td>
          <td class="text-center">
            <button class="btn btn-sm btn-outline-primary me-1" @click="openEdit(u)">수정</button>
            <button class="btn btn-sm btn-outline-danger" @click="confirmDelete(u)">삭제</button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- 페이지네이션 -->
    <div v-if="users.length > 0" class="d-flex align-items-center justify-content-between mt-3">
      <small class="text-muted">
        페이지 {{ pagination.currentPage }} / {{ pagination.totalPages }} 
        (전체 {{ pagination.totalCount }}명)
      </small>
      <nav>
        <ul class="pagination pagination-sm mb-0">
          <li :class="['page-item', { disabled: pagination.currentPage === 1 }]">
            <button class="page-link" :disabled="pagination.currentPage === 1" @click="goToPage(1)">
              처음
            </button>
          </li>
          <li :class="['page-item', { disabled: pagination.currentPage === 1 }]">
            <button class="page-link" :disabled="pagination.currentPage === 1" @click="goToPage(pagination.currentPage - 1)">
              이전
            </button>
          </li>
          
          <li 
            v-for="p in visiblePages" 
            :key="p"
            :class="['page-item', { active: p === pagination.currentPage }]"
          >
            <button class="page-link" @click="goToPage(p)">
              {{ p }}
            </button>
          </li>

          <li :class="['page-item', { disabled: pagination.currentPage === pagination.totalPages }]">
            <button class="page-link" :disabled="pagination.currentPage === pagination.totalPages" @click="goToPage(pagination.currentPage + 1)">
              다음
            </button>
          </li>
          <li :class="['page-item', { disabled: pagination.currentPage === pagination.totalPages }]">
            <button class="page-link" :disabled="pagination.currentPage === pagination.totalPages" @click="goToPage(pagination.totalPages)">
              끝
            </button>
          </li>
        </ul>
      </nav>
    </div>

    <!-- 수정 모달 -->
    <div v-if="editModal" class="modal-backdrop-custom">
      <div class="modal-box" @click.stop>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="fw-bold mb-0">사용자 수정 — {{ editForm.UserID }}</h6>
          <button class="btn-close" @click="editModal = false"></button>
        </div>

        <div class="mb-2">
          <label class="form-label form-label-sm">이름 <span class="text-danger">*</span></label>
          <input v-model="editForm.Name" type="text" class="form-control form-control-sm" maxlength="50" />
        </div>
        <div class="mb-2">
          <label class="form-label form-label-sm">전화번호</label>
          <input v-model="editForm.Tel" type="text" class="form-control form-control-sm" maxlength="20" />
        </div>
        <div class="mb-2">
          <label class="form-label form-label-sm">차량번호 <span class="text-danger">*</span></label>
          <input v-model="editForm.CarNumber" type="text" class="form-control form-control-sm" maxlength="20" />
        </div>
        <div class="mb-2">
          <label class="form-label form-label-sm">구분 <span class="text-danger">*</span></label>
          <select v-model="editForm.UserType" class="form-select form-select-sm">
            <option value="USER">USER</option>
            <option value="ADMIN">ADMIN</option>
          </select>
        </div>
        <div class="mb-2">
          <label class="form-label form-label-sm">신규 / 기존</label>
          <select v-model="editForm.IsExistingUser" class="form-select form-select-sm">
            <option :value="false">신규</option>
            <option :value="true">기존</option>
          </select>
        </div>
        <div class="mb-3">
          <label class="form-label form-label-sm">새 비밀번호 <span class="text-muted">(변경 시만 입력)</span></label>
          <input v-model="editForm.newPassword" type="password" class="form-control form-control-sm" />
        </div>

        <div v-if="editError" class="alert alert-danger py-1 small">{{ editError }}</div>

        <div class="d-flex gap-2 justify-content-end">
          <button class="btn btn-secondary btn-sm" @click="editModal = false">취소</button>
          <button class="btn btn-primary btn-sm" :disabled="saving" @click="saveEdit">
            {{ saving ? '저장 중...' : '저장' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 삭제 확인 모달 -->
    <div v-if="deleteModal" class="modal-backdrop-custom">
      <div class="modal-box" @click.stop>
        <h6 class="fw-bold mb-2">사용자 삭제</h6>
        <p class="mb-3">
          <strong>{{ deleteTarget?.UserID }}</strong> ({{ deleteTarget?.Name }}) 계정을 삭제합니다.<br/>
          <span class="text-danger small">이 작업은 되돌릴 수 없습니다.</span>
        </p>
        <div v-if="deleteError" class="alert alert-danger py-1 small">{{ deleteError }}</div>
        <div class="d-flex gap-2 justify-content-end">
          <button class="btn btn-secondary btn-sm" @click="deleteModal = false">취소</button>
          <button class="btn btn-danger btn-sm" :disabled="saving" @click="doDelete">
            {{ saving ? '삭제 중...' : '삭제' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'UserManagementView',
  data() {
    return {
      users: [],
      loading: false,
      loadError: null,
      searchText: '',
      searchQuery: '',
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        pageSize: 20
      },
      editModal: false,
      editForm: { UserSeq: null, UserID: '', Name: '', Tel: '', CarNumber: '', UserType: 'USER', IsExistingUser: false, newPassword: '' },
      editError: null,
      editSuccess: null,
      deleteModal: false,
      deleteTarget: null,
      deleteError: null,
      saving: false
    }
  },
  mounted() {
    this.fetchUsers()
  },
  computed: {
    visiblePages() {
      const total = this.pagination.totalPages;
      const current = this.pagination.currentPage;
      const range = 5; // 양쪽으로 5페이지씩 표시
      let start = Math.max(1, current - range);
      let end = Math.min(total, current + range);
      
      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  methods: {
    async fetchUsers(page = 1) {
      this.loading = true
      this.loadError = null
      try {
        const params = { page };
        if (this.searchQuery) {
          params.search = this.searchQuery;
        }
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/admin/users`, { params })
        this.users = res.data.data;
        this.pagination = {
          currentPage: res.data.currentPage,
          totalPages: res.data.totalPages,
          totalCount: res.data.totalCount,
          pageSize: res.data.pageSize
        }
      } catch {
        this.loadError = '사용자 목록을 불러오지 못했습니다.'
      } finally {
        this.loading = false
      }
    },
    search() {
      this.searchQuery = this.searchText.trim();
      this.fetchUsers(1);
    },
    resetSearch() {
      this.searchText = '';
      this.searchQuery = '';
      this.fetchUsers(1);
    },
    goToPage(page) {
      if (page >= 1 && page <= this.pagination.totalPages) {
        this.fetchUsers(page);
      }
    },
    openEdit(u) {
      this.editForm = {
        UserSeq: u.UserSeq,
        UserID: u.UserID,
        Name: u.Name,
        Tel: u.Tel || '',
        CarNumber: u.CarNumber || '',
        UserType: u.UserType,
        IsExistingUser: !!u.IsExistingUser,
        newPassword: ''
      }
      this.editError = null
      this.editSuccess = null
      this.editModal = true
    },
    async saveEdit() {
      if (!this.editForm.Name.trim()) { this.editError = '이름을 입력하세요.'; return }
      if (!this.editForm.Tel || !this.editForm.Tel.trim()) { this.editError = '전화번호를 입력하세요.'; return }
      if (!this.editForm.CarNumber || !this.editForm.CarNumber.trim()) { this.editError = '차량번호를 입력하세요.'; return }
      this.saving = true
      this.editError = null
      try {
        await axios.put(`${process.env.VUE_APP_API_BASE}/api/admin/users/${this.editForm.UserSeq}`, {
          name: this.editForm.Name,
          tel: this.editForm.Tel,
          carNumber: this.editForm.CarNumber,
          userType: this.editForm.UserType,
          isExistingUser: !!this.editForm.IsExistingUser,
          newPassword: this.editForm.newPassword || null
        })
        this.editModal = false
        this.fetchUsers(this.pagination.currentPage);
        this.editSuccess = '수정이 완료되었습니다.'
        setTimeout(() => { this.editSuccess = null }, 3000)
      } catch (e) {
        this.editError = e.response?.data?.message || '저장에 실패했습니다.'
      } finally {
        this.saving = false
      }
    },
    confirmDelete(u) {
      this.deleteTarget = u
      this.deleteError = null
      this.deleteModal = true
    },
    async doDelete() {
      this.saving = true
      this.deleteError = null
      try {
        await axios.delete(`${process.env.VUE_APP_API_BASE}/api/admin/users/${this.deleteTarget.UserSeq}`)
        this.deleteModal = false
        this.fetchUsers(this.pagination.currentPage);
      } catch (e) {
        this.deleteError = e.response?.data?.message || '삭제에 실패했습니다.'
      } finally {
        this.saving = false
      }
    },
    formatDate(val) {
      if (!val) return '-'
      return new Date(val).toLocaleDateString('ko-KR')
    }
  }
}
</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-box {
  background: #fff;
  border-radius: 10px;
  padding: 1.5rem;
  width: 420px;
  max-width: 95vw;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
}
</style>
